import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { showHubTeamToast } from "../utils/hub_team_toast"
import { selectionFormData, selectedMemberCount, reloadRoster } from "../utils/roster_selection"

const COPY = {
  invite: {
    title: "Send invitations",
    action: "Send invitations",
    nobody: "Nobody in this selection is waiting on a first invitation.",
    breakdown: [
      ["Will be emailed", "eligible_count"],
      ["Already invited, will not be emailed again", "awaiting_acceptance_count"],
      ["Already accepted, will not be emailed", "accepted_count"]
    ]
  },
  schedule: {
    title: "Schedule invitations",
    action: "Schedule invitations",
    nobody: "Nobody in this selection is waiting on a first invitation.",
    breakdown: [
      ["Will be scheduled", "not_invited_count"],
      ["Already scheduled, date will be updated", "scheduled_count"],
      ["Already invited, will not be emailed again", "awaiting_acceptance_count"],
      ["Already accepted, will not be emailed", "accepted_count"]
    ]
  },
  remind: {
    title: "Send a reminder",
    action: "Send reminders",
    nobody: "Nobody in this selection is waiting to accept an invitation.",
    breakdown: [
      ["Will be reminded", "eligible_count"],
      ["Not invited yet, will not be emailed", "not_invited_count"],
      ["Already accepted, will not be emailed", "accepted_count"]
    ]
  }
}

/**
 * Batch Invite Modal Controller
 *
 * Confirms a batch send against a count the server worked out for the real selection — which may
 * be "everyone matching these filters" — rather than the number of rows on screen, then reports
 * back how many went out and who was skipped or failed.
 */
export default class extends Controller {
  static targets = [
    "modal",
    "title",
    "description",
    "breakdown",
    "confirmation",
    "results",
    "resultsSummary",
    "outcomes",
    "submitButton",
    "cancelButton",
    "lineTemplate",
    "timingFields",
    "timingInput",
    "dateField",
    "dateInput"
  ]

  static values = { inviteUrl: String, remindUrl: String, previewUrl: String }

  async open(event) {
    event.preventDefault()

    this.mode = event.currentTarget.dataset.batchInviteMode || "invite"
    if (selectedMemberCount(this) === 0) return

    this.resetTiming()
    this.showConfirmation()
    Modal.getOrCreateInstance(this.modalTarget).show()
    await this.loadPreview()
  }

  timingChanged() {
    const later = this.schedulingLater()

    if (this.hasDateFieldTarget) this.dateFieldTarget.hidden = !later
    if (this.hasDateInputTarget && !later) this.dateInputTarget.value = ""

    if (this.preview) this.renderBreakdown(this.preview)
  }

  async loadPreview() {
    const formData = selectionFormData(this)
    formData.append("mode", this.mode)

    const data = await this.post(this.previewUrlValue, formData, "Could not count the selection")
    if (!data) return this.close()

    this.eligibleCount = data.eligible_count
    this.preview = data
    this.renderBreakdown(data)
  }

  async send() {
    if (!this.eligibleCount) return
    if (this.schedulingLater() && !this.selectedDate()) return

    this.setSending(true)
    const formData = selectionFormData(this)
    if (this.schedulingLater()) formData.append("invitation_scheduled_on", this.selectedDate())

    const data = await this.post(this.sendUrl(), formData, this.failurePrefix())
    this.setSending(false)

    if (!data) return

    this.renderResults(data)
    this.refreshRoster()
  }

  // --- rendering ---------------------------------------------------------------

  showConfirmation() {
    this.eligibleCount = null
    this.preview = null
    this.titleTarget.textContent = this.copy().title
    this.descriptionTarget.textContent = this.mode === "remind"
      ? "Counting who will be emailed..."
      : "Counting who will be invited..."
    this.breakdownTarget.hidden = true
    this.confirmationTarget.hidden = false
    this.resultsTarget.hidden = true
    this.outcomesTarget.hidden = true
    this.submitButtonTarget.hidden = false
    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.textContent = this.copy().action
    this.cancelButtonTarget.textContent = "Cancel"
    this.toggleTimingFields()
  }

  renderBreakdown(preview) {
    const eligible = preview.eligible_count
    const verb = this.schedulingLater() ? "scheduled" : "emailed"

    this.descriptionTarget.textContent = eligible === 0
      ? this.copy().nobody
      : `${eligible} of the ${this.memberPhrase(preview.selected_count)} you selected will be ${verb}.`

    this.replaceLines(this.breakdownTarget, this.copy().breakdown
      .filter(([, key]) => preview[key] > 0)
      .map(([label, key]) => [label, String(preview[key])]))

    const canSubmit = eligible > 0 && (!this.schedulingLater() || Boolean(this.selectedDate()))
    this.submitButtonTarget.disabled = !canSubmit
    this.submitButtonTarget.textContent = eligible === 0 ? this.copy().action : `${this.copy().action} (${eligible})`
  }

  renderResults(result) {
    const skipped = result.skipped || []
    const failed = result.failed || []
    const scheduled = Boolean(result.scheduled)

    this.confirmationTarget.hidden = true
    this.resultsTarget.hidden = false
    this.submitButtonTarget.hidden = true
    this.cancelButtonTarget.textContent = "Done"

    if (scheduled) {
      this.resultsSummaryTarget.textContent = `${this.memberPhrase(result.scheduled_count)} scheduled.`
    } else {
      this.resultsSummaryTarget.textContent = failed.length === 0
        ? `${this.memberPhrase(result.sent_count)} emailed.`
        : `${this.memberPhrase(result.sent_count)} emailed, ${failed.length} could not be sent.`
    }

    this.replaceLines(this.outcomesTarget, [
      ...failed.map((entry) => [entry.name || entry.email, entry.reason]),
      ...skipped.map((entry) => [entry.name || entry.email, entry.reason])
    ])

    if (failed.length === 0 && skipped.length === 0) {
      showHubTeamToast(this.resultsSummaryTarget.textContent)
    }
  }

  replaceLines(list, lines) {
    list.replaceChildren()

    lines.forEach(([label, value]) => {
      const line = this.lineTemplateTarget.content.cloneNode(true)
      line.querySelector(".hub-team-batch-invite-line__label").textContent = label
      line.querySelector(".hub-team-batch-invite-line__value").textContent = value
      list.appendChild(line)
    })

    list.hidden = lines.length === 0
  }

  // --- plumbing ----------------------------------------------------------------

  copy() {
    if (this.mode === "remind") return COPY.remind
    if (this.schedulingLater()) return COPY.schedule

    return COPY.invite
  }

  sendUrl() {
    return this.mode === "remind" ? this.remindUrlValue : this.inviteUrlValue
  }

  failurePrefix() {
    if (this.mode === "remind") return "Failed to send reminders"
    if (this.schedulingLater()) return "Failed to schedule invitations"

    return "Failed to send invitations"
  }

  schedulingLater() {
    if (this.mode === "remind" || !this.hasTimingInputTarget) return false

    return this.timingInputTargets.some((input) => input.checked && input.value === "later")
  }

  selectedDate() {
    return this.hasDateInputTarget ? this.dateInputTarget.value : ""
  }

  toggleTimingFields() {
    if (!this.hasTimingFieldsTarget) return

    this.timingFieldsTarget.hidden = this.mode !== "invite"
    this.timingChanged()
  }

  resetTiming() {
    if (this.hasTimingInputTarget) {
      this.timingInputTargets.forEach((input) => {
        input.checked = input.value === "now"
      })
    }

    if (this.hasDateInputTarget) this.dateInputTarget.value = ""
    if (this.hasDateFieldTarget) this.dateFieldTarget.hidden = true
  }

  memberPhrase(count) {
    return count === 1 ? "1 member" : `${count} members`
  }

  setSending(sending) {
    this.submitButtonTarget.disabled = sending
    if (sending) this.submitButtonTarget.textContent = "Sending..."
  }

  close() {
    Modal.getOrCreateInstance(this.modalTarget).hide()
  }

  // A send changes status badges, the awaiting-invitation count and the row actions at once, so
  // the frame is reloaded rather than those states being reproduced in JavaScript.
  refreshRoster() {
    reloadRoster(this)
  }

  async post(url, formData, errorPrefix) {
    try {
      const response = await fetch(url, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        },
        credentials: "same-origin"
      })

      const data = await response.json()
      if (response.ok && data.success) return data

      showHubTeamToast(`${errorPrefix}: ${data?.errors?.join(", ") || `HTTP ${response.status}`}`, {
        variant: "error"
      })
    } catch (error) {
      console.error(errorPrefix, error)
      showHubTeamToast("An error occurred. Please try again.", { variant: "error" })
    }

    return null
  }
}
